import { useEffect, useContext, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Link,
  Button,
  IconButton,
  Separator,
} from "@chakra-ui/react";
import { Menu, X, FileText, Receipt, User, LogOut, LogIn, UserPlus, Shield } from "lucide-react";
import { LanguageContext } from "../contexts/LanguageContext";
import { navbarTexts } from "../i18n/navbar";
import LanguageSwitcher from "./LanguageSwitcher";

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Space Grotesk letterspaced uppercase — same nav treatment as the shop
const navLinkStyles = {
  fontFamily: "space",
  fontSize: "xs",
  fontWeight: "500",
  letterSpacing: "0.14em",
  textTransform: "uppercase",
};

const Navbar = ({ isLoggedIn, setIsLoggedIn }) => {
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const [isOpen, setIsOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const t = navbarTexts[language];

  const onClose = () => setIsOpen(false);
  const onOpen = () => setIsOpen(true);

  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/check_login/`, {
          method: "GET",
          credentials: "include",
        });
        setIsLoggedIn(response.ok);
        if (response.ok) {
          const data = await response.json();
          setIsAdmin(!!data.is_staff);
        } else {
          setIsAdmin(false);
        }
      } catch {
        setIsLoggedIn(false);
        setIsAdmin(false);
      }
    };
    checkLoginStatus();
    // re-run after SPA login/logout so the admin flag stays in sync
  }, [setIsLoggedIn, isLoggedIn]);

  const NavLinks = ({ isMobile = false }) => (
    <>
      {isLoggedIn ? (
        <>
          {isAdmin && (
            <Link
              as={RouterLink}
              to="/admin"
              {...navLinkStyles}
              display="flex"
              alignItems="center"
              gap={2}
              p={isMobile ? 3 : 0}
              borderRadius={isMobile ? "md" : 0}
              color="orange.300"
              _hover={{ color: "orange.200", bg: isMobile ? "whiteAlpha.200" : "transparent", textDecoration: "none" }}
              transition="all 0.2s"
              onClick={isMobile ? onClose : undefined}
            >
              <Shield size={16} />
              ADMIN
            </Link>
          )}
          <Link
            as={RouterLink}
            to="/contracts"
            {...navLinkStyles}
            display="flex"
            alignItems="center"
            gap={2}
            p={isMobile ? 3 : 0}
            borderRadius={isMobile ? "md" : 0}
            color="gray.300"
            _hover={{ color: "white", bg: isMobile ? "whiteAlpha.200" : "transparent", textDecoration: "none" }}
            transition="all 0.2s"
            onClick={isMobile ? onClose : undefined}
          >
            <FileText size={16} />
            {t.contracts}
          </Link>
          <Link
            as={RouterLink}
            to="/invoices"
            {...navLinkStyles}
            display="flex"
            alignItems="center"
            gap={2}
            p={isMobile ? 3 : 0}
            borderRadius={isMobile ? "md" : 0}
            color="gray.300"
            _hover={{ color: "white", bg: isMobile ? "whiteAlpha.200" : "transparent", textDecoration: "none" }}
            transition="all 0.2s"
            onClick={isMobile ? onClose : undefined}
          >
            <Receipt size={16} />
            {t.invoices}
          </Link>
          <Link
            as={RouterLink}
            to="/profile"
            {...navLinkStyles}
            display="flex"
            alignItems="center"
            gap={2}
            p={isMobile ? 3 : 0}
            borderRadius={isMobile ? "md" : 0}
            color="gray.300"
            _hover={{ color: "white", bg: isMobile ? "whiteAlpha.200" : "transparent", textDecoration: "none" }}
            transition="all 0.2s"
            onClick={isMobile ? onClose : undefined}
          >
            <User size={16} />
            {t.profile}
          </Link>
          {isMobile && <Separator borderColor="whiteAlpha.300" />}
          <Button
            size="sm"
            {...navLinkStyles}
            onClick={() => {
              navigate("/logout");
              if (isMobile) onClose();
            }}
            variant={isMobile ? "ghost" : "solid"}
            bg={isMobile ? "transparent" : "white"}
            color={isMobile ? "white" : "#18181b"}
            _hover={{
              bg: isMobile ? "whiteAlpha.200" : "gray.200",
            }}
            w={isMobile ? "full" : "auto"}
            justifyContent={isMobile ? "flex-start" : "center"}
          >
            <LogOut size={14} />
            {t.logout}
          </Button>
        </>
      ) : (
        <>
          <Button
            as={RouterLink}
            to="/register"
            {...navLinkStyles}
            variant={isMobile ? "ghost" : "outline"}
            size="sm"
            borderColor={isMobile ? "transparent" : "whiteAlpha.500"}
            color="white"
            _hover={{
              bg: isMobile ? "whiteAlpha.200" : "whiteAlpha.300",
            }}
            w={isMobile ? "full" : "auto"}
            justifyContent={isMobile ? "flex-start" : "center"}
            onClick={isMobile ? onClose : undefined}
          >
            <UserPlus size={14} />
            {t.register}
          </Button>
          <Button
            as={RouterLink}
            to="/login"
            {...navLinkStyles}
            size="sm"
            bg={isMobile ? "transparent" : "white"}
            color={isMobile ? "white" : "#18181b"}
            variant={isMobile ? "ghost" : "solid"}
            _hover={{
              bg: isMobile ? "whiteAlpha.200" : "gray.200",
            }}
            w={isMobile ? "full" : "auto"}
            justifyContent={isMobile ? "flex-start" : "center"}
            onClick={isMobile ? onClose : undefined}
          >
            <LogIn size={14} />
            {t.login}
          </Button>
        </>
      )}
    </>
  );

  return (
    <Box bg="#09090b" px={{ base: 4, md: 6 }} borderBottomWidth="1px" borderColor="whiteAlpha.200">
      <Flex h={16} alignItems="center" justifyContent="space-between">
        {/* Logo / Left */}
        <Link
          as={RouterLink}
          to="/"
          {...navLinkStyles}
          color="gray.200"
          _hover={{ textDecoration: "none", color: "white" }}
          transition="color 0.2s"
        >
          {t.available}
        </Link>
        <Link
          as={RouterLink}
          to="/faq"
          {...navLinkStyles}
          color="gray.400"
          _hover={{ textDecoration: "none", color: "white" }}
          transition="color 0.2s"
        >
          {t.faq}
        </Link>

        {/* Desktop */}
        <HStack
          gap={6}
          alignItems="center"
          display={{ base: "none", md: "flex" }}
          color="white"
        >
          <NavLinks />
          <LanguageSwitcher />
        </HStack>

        {/* Mobile menu button */}
        <IconButton
          display={{ base: "flex", md: "none" }}
          onClick={isOpen ? onClose : onOpen}
          variant="ghost"
          color="white"
          aria-label="Toggle menu"
          _hover={{ bg: "whiteAlpha.200" }}
          size="lg"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </IconButton>
      </Flex>

      {/* Mobile dropdown with animation */}
      <Box
        display={{ md: "none" }}
        overflow="hidden"
        maxH={isOpen ? "500px" : "0"}
        transition="max-height 0.3s ease-in-out"
      >
        <VStack
          pb={4}
          pt={2}
          gap={2}
          align="stretch"
          color="white"
        >
          <NavLinks isMobile={true} />
          <Separator borderColor="whiteAlpha.300" />
          <Box px={3} py={2}>
            <LanguageSwitcher />
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default Navbar;
